import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Feature, MultiPolygon, Polygon, Position } from 'geojson'
import {
  featureCollection as createFeatureCollection,
  polygon as createPolygon,
} from '@turf/helpers'
import calculateArea from '@turf/area'
import memoize from 'fast-memoize'
import { colors } from '~/design'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import GeoJSON from './geoJson'
import { truthy } from '~/prix'
import Marker from './marker'
import kinks from '@turf/kinks'

export type DemarcationPoint = google.maps.LatLngLiteral & {
  id: string
}
export type GhostDemarcationPoint = DemarcationPoint & {
  previousId: string
}

export type PolygonValidationResult = { errorMessage: string; status: boolean }

function getPolygonPoints(polygon: Feature<Polygon> | null): Array<DemarcationPoint> {
  if (!polygon) {
    return []
  }

  const outerRing = polygon.geometry.coordinates[0]
  if (!outerRing) {
    return []
  }

  return outerRing
    .filter((_, index, list) => index < list.length - 1)
    .map((point, index) => ({
      id: String(index),
      lat: point[1],
      lng: point[0],
    }))
}

function getLargestPolygonCoordinates(multiPolygon: MultiPolygon): Position[][] | null {
  let largestPolygonCoordinates: Position[][] | null = null
  let largestPolygonArea = Number.NEGATIVE_INFINITY

  for (const polygonCoordinates of multiPolygon.coordinates) {
    const polygon = createPolygon(polygonCoordinates)
    const polygonArea = calculateArea(polygon)

    if (polygonArea > largestPolygonArea) {
      largestPolygonCoordinates = polygonCoordinates
      largestPolygonArea = polygonArea
    }
  }

  return largestPolygonCoordinates
}

function getEditableInitialPolygon(
  polygon: Feature<Polygon | MultiPolygon> | null,
): Feature<Polygon> | null {
  if (!polygon) {
    return null
  }

  if (polygon.geometry.type === 'Polygon') {
    return polygon as Feature<Polygon>
  }

  const largestPolygonCoordinates = getLargestPolygonCoordinates(polygon.geometry)
  if (!largestPolygonCoordinates) {
    return null
  }

  return {
    ...polygon,
    geometry: {
      type: 'Polygon',
      coordinates: largestPolygonCoordinates,
    },
  }
}

export function usePolygonEditor(initialPolygon: Feature<Polygon | MultiPolygon> | null = null) {
  const nextMarkerIdRef = useRef(0)
  const editableInitialPolygon = useMemo(
    () => getEditableInitialPolygon(initialPolygon),
    [initialPolygon],
  )
  const [points, setPointsState] = useState<Array<DemarcationPoint>>(() => {
    const initialPoints = getPolygonPoints(editableInitialPolygon)
    nextMarkerIdRef.current = initialPoints.length
    return initialPoints
  })
  const [isDirty, setIsDirty] = useState(false)
  const [activeGhostPoint, setActiveGhostPoint] = useState<GhostDemarcationPoint | null>(null)

  const updateNextMarkerIdFromPoints = useCallback((nextPoints: Array<DemarcationPoint>) => {
    let maxNumericId = -1
    for (const point of nextPoints) {
      const numericId = Number(point.id)
      if (Number.isFinite(numericId)) {
        maxNumericId = Math.max(maxNumericId, numericId)
      }
    }
    const nextCandidate = maxNumericId >= 0 ? maxNumericId + 1 : nextPoints.length
    nextMarkerIdRef.current = Math.max(nextMarkerIdRef.current, nextCandidate)
  }, [])

  const setPoints = useCallback(
    (
      updater:
        | Array<DemarcationPoint>
        | ((previousPoints: Array<DemarcationPoint>) => Array<DemarcationPoint>),
    ) => {
      setPointsState(previousPoints => {
        const nextPoints = typeof updater === 'function' ? updater(previousPoints) : updater
        updateNextMarkerIdFromPoints(nextPoints)
        return nextPoints
      })
    },
    [updateNextMarkerIdFromPoints],
  )

  useEffect(() => {
    setPoints(getPolygonPoints(editableInitialPolygon))
  }, [editableInitialPolygon, setPoints])

  const polygonPoints = useMemo(() => {
    // Points with active ghost point
    if (activeGhostPoint) {
      const index = points.findIndex(p => p.id === activeGhostPoint.previousId)
      const newPoints = Array.from(points)
      newPoints.splice(index + 1, 0, activeGhostPoint)
      return newPoints
    }
    return points
  }, [points, activeGhostPoint])

  const ghostPolygonPoints = useMemo(() => {
    if (points.length < 2) {
      return []
    }
    return points.map((point, index, list) => {
      const nextIndex = index < list.length - 1 ? index + 1 : 0
      const nextPoint = list[nextIndex]
      const id = `${point.id}-${nextPoint.id}`
      const lng = (point.lng + nextPoint.lng) / 2
      const lat = (point.lat + nextPoint.lat) / 2

      return {
        id,
        lat,
        lng,
        previousId: point.id,
      } as GhostDemarcationPoint
    })
  }, [points])

  const handleReset = useCallback(() => {
    setIsDirty(false)
    setPoints(getPolygonPoints(editableInitialPolygon))
  }, [editableInitialPolygon, setPoints])

  useEffect(() => {
    handleReset()
  }, [handleReset])

  const { polygon, polygonIsValid } = useMemo(() => {
    let validationResult: PolygonValidationResult = { errorMessage: '', status: true }

    if (!isDirty) {
      return { polygon: editableInitialPolygon, polygonIsValid: validationResult }
    }
    if (polygonPoints.length < 3) {
      return { polygon: null, polygonIsValid: validationResult }
    }
    const coordinates = [
      ...polygonPoints.map(point => [point.lng, point.lat]),
      [polygonPoints[0].lng, polygonPoints[0].lat],
    ]
    const polygon = createPolygon([coordinates])
    const selfIntersections = kinks(polygon)

    if (selfIntersections.features.length > 0) {
      validationResult = {
        errorMessage: 'Demarcação inválida. Verifique se há sobreposição de linhas.',
        status: false,
      }
    }
    return { polygon, polygonIsValid: validationResult }
  }, [polygonPoints, isDirty, editableInitialPolygon])

  const appendPoint = useCallback(
    (position: google.maps.LatLngLiteral) => {
      setPoints(points => [
        ...points,
        {
          id: String(nextMarkerIdRef.current++),
          ...position,
        },
      ])
      setIsDirty(true)
    },
    [setPoints],
  )

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const { latLng } = event
      if (!latLng) {
        return
      }

      appendPoint(latLng.toJSON())
    },
    [appendPoint],
  )

  const handleMarkerDragEnd = useCallback(
    memoize((point: DemarcationPoint, index: number) => (event: google.maps.MapMouseEvent) => {
      const { latLng } = event
      if (!latLng) {
        return
      }
      setPoints(points => {
        const newPoints = Array.from(points)
        newPoints[index] = {
          ...point,
          ...latLng.toJSON(),
        }
        return newPoints
      })
      setIsDirty(true)
    }),
    [],
  )

  const handleMarkerClick = useCallback(
    memoize((point: DemarcationPoint) => (_event: google.maps.MapMouseEvent) => {
      setActiveGhostPoint(null)
      setPoints(points => points.filter(currentPoint => currentPoint.id !== point.id))
      setIsDirty(true)
    }),
    [],
  )

  const onMarkerClick = useCallback(
    (point: DemarcationPoint, _index: number) => {
      return handleMarkerClick(point)
    },
    [handleMarkerClick],
  )

  const handleGhostMarkerDragEnd = useCallback(
    memoize((point: GhostDemarcationPoint) => (event: google.maps.MapMouseEvent) => {
      const { latLng } = event

      // Include ghost point in polygon
      setActiveGhostPoint(null)
      setPoints(points => {
        const newPoints = Array.from(points)
        const index = points.findIndex(p => p.id === point.previousId)
        if (index === -1) {
          return points
        }
        newPoints.splice(index + 1, 0, {
          ...point,
          ...(latLng ? latLng.toJSON() : {}),
        })
        return newPoints
      })
      setIsDirty(true)
    }),
    [],
  )

  return {
    points,
    setPoints,
    setIsDirty,
    setActiveGhostPoint,
    appendPoint,
    polygon,
    polygonPoints,
    ghostPolygonPoints,
    polygonIsValid,
    onMapClick: handleMapClick,
    onReset: handleReset,
    onMarkerDragEnd: handleMarkerDragEnd,
    onMarkerClick,
    onGhostMarkerDragEnd: handleGhostMarkerDragEnd,
  }
}

function PolygonEditor({
  points,
  polygon,
  ghostPolygonPoints,
  onMarkerDragEnd,
  onMarkerClick,
  onGhostMarkerDragEnd,
  onMapClick,
  markerStyle,
  ghostMarkerStyle,
  polygonOptions,
}: {
  points: Array<DemarcationPoint>
  polygon: Feature<Polygon> | null
  ghostPolygonPoints: Array<GhostDemarcationPoint>
  onMarkerDragEnd: (
    point: DemarcationPoint,
    index: number,
  ) => (event: google.maps.MapMouseEvent) => void
  onMarkerClick?: (
    point: DemarcationPoint,
    index: number,
  ) => (event: google.maps.MapMouseEvent) => void
  onGhostMarkerDragEnd: (point: GhostDemarcationPoint) => (event: google.maps.MapMouseEvent) => void
  onMapClick: (event: google.maps.MapMouseEvent) => void
  markerStyle?: google.maps.Symbol | google.maps.Icon
  ghostMarkerStyle?: google.maps.Symbol | google.maps.Icon
  polygonOptions?: google.maps.Data.StyleOptions
}) {
  const { google } = useGoogleMaps()
  const {
    markerStyle: finalMarkerStyle,
    ghostMarkerStyle: finalGhostMarkerStyle,
    polygonOptions: finalPolygonOptions,
  } = useMemo(() => {
    if (!google) {
      return {
        markerStyle: undefined,
        ghostMarkerStyle: undefined,
      }
    }

    const suggestionMarkerStyle = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: colors.prixOrange,
      fillOpacity: 1,
      strokeColor: colors.gray,
      strokeWeight: 2,
    }
    const suggestionGhostMarkerStyle = {
      ...suggestionMarkerStyle,
      scale: 6,
      strokeColor: colors.prixOrange,
    }
    const suggestionPolygonOptions = {
      fillColor: colors.prixOrange,
      fillOpacity: 0.2,
      strokeColor: colors.prixOrange,
      strokeOpacity: 1,
      strokeWeight: 2,
      zIndex: 1,
    }
    return {
      markerStyle: markerStyle || suggestionMarkerStyle,
      ghostMarkerStyle: ghostMarkerStyle || suggestionGhostMarkerStyle,
      polygonOptions: polygonOptions || suggestionPolygonOptions,
    }
  }, [google, markerStyle, ghostMarkerStyle, polygonOptions])

  const featureCollection = useMemo(() => {
    if (!google) {
      return null
    }
    return createFeatureCollection([polygon].filter(truthy))
  }, [points])

  return (
    <>
      {featureCollection ? (
        <GeoJSON
          geoJson={featureCollection}
          onClick={onMapClick}
          dataMapStyle={finalPolygonOptions}
        />
      ) : null}

      {points.map((point, index) => (
        <Marker
          key={point.id}
          position={point}
          customIcon={finalMarkerStyle}
          onClick={onMarkerClick ? onMarkerClick(point, index) : undefined}
          onDragEnd={onMarkerDragEnd(point, index)}
        />
      ))}
      {ghostPolygonPoints.map((point, index) => (
        <Marker
          key={point.id}
          position={point}
          customIcon={finalGhostMarkerStyle}
          onDragEnd={onGhostMarkerDragEnd(point)}
        />
      ))}
    </>
  )
}

export default memo(PolygonEditor)
